import Slider from "components/DesignSystem/Slider/Slider";
import Table from "components/DesignSystem/Table/V2/Table";
import { useMemo } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { ModalProps } from "types/utils/modal";
import { CircularFlag } from "../Task1120Columns/NoOfSubsidiaries";
import { TaskRelatedFile } from "../Task1120Columns/TaskRelatedFile";
import { NonUsSubsidiary } from "types/Models/TaskTracker";

type NonUsSubsidiaryBooksSliderProps = ModalProps & {
  nonUsSubsidiaryBooks: NonUsSubsidiary[];
  groupName: string;
  taskName: string;
};

export const NonUsSubsidiaryBooksSlider = ({
  nonUsSubsidiaryBooks,
  isOpen,
  close,
  groupName,
  taskName,
}: NonUsSubsidiaryBooksSliderProps) => {
  const columnHelper = createColumnHelper<NonUsSubsidiary>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: ({ row }) => (
          <div className="t-flex t-items-center t-gap-1">
            <CircularFlag countryCode={row.original.country.code} />
            <span className="t-text-body-sm t-text-text-100">
              {row.original.name}
            </span>
          </div>
        ),
        header: "NON US Subsidiary",
        size: 35,
      }),
      columnHelper.accessor("income_statement", {
        cell: ({ row }) => {
          if (
            !row.original.income_statement ||
            row.original.income_statement.length === 0
          ) {
            return "-";
          }
          return (
            <div className="t-flex t-items-center t-gap-1">
              {row.original.income_statement?.map(
                ({ uuid, file_type, group_id }) => (
                  <TaskRelatedFile
                    fileId={uuid}
                    fileType={file_type}
                    key={uuid}
                    groupId={group_id}
                  />
                )
              ) || "-"}
            </div>
          );
        },
        header: "Income Statement",
        size: 35,
      }),
      columnHelper.accessor("balance_sheet", {
        cell: ({ row }) => {
          if (
            !row.original.balance_sheet ||
            row.original.balance_sheet.length === 0
          ) {
            return "-";
          }
          return (
            <div className="t-flex t-items-center t-gap-1">
              {row.original.balance_sheet?.map(
                ({ uuid, file_type, group_id }) => (
                  <TaskRelatedFile
                    fileId={uuid}
                    fileType={file_type}
                    key={uuid}
                    groupId={group_id}
                  />
                )
              )}
            </div>
          );
        },
        header: "Balance Sheet",
        size: 30,
      }),
    ],
    []
  );
  const table = useReactTable({
    data: nonUsSubsidiaryBooks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
    },
    enableSorting: false,
  });
  return (
    <Slider.Root open={isOpen} onOpenChange={close}>
      <Slider.Content open={isOpen}>
        <Slider.Header>
          <Slider.Title>{taskName || "-"}</Slider.Title>
          <Slider.Close />
        </Slider.Header>
        <Slider.Body>
          <div className="t-mb-6">{groupName || "-"}</div>
          <Table.Container>
            <Table.Content>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.HeadCell
                        key={header.id}
                        style={{ width: `${header.getSize()}%` }}
                        className="t-py-3"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.HeadCell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Head>
              <Table.Body>
                {table.getRowModel().rows.map((row) => (
                  <Table.Row key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}%` }}
                        className="t-py-3"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.Container>
        </Slider.Body>
      </Slider.Content>
    </Slider.Root>
  );
};
